import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { userApi } from "../../api/user-api";
import { sessionsApi } from "../../api/session-api";
import { surveyApi } from "../../api/survey-api";
import { formationsApi } from "../../api/formation-api";
import { CHUNK_PRELOADERS } from "../../modules/routes";
import ForumIcon from '@mui/icons-material/Forum';
import {
  Box,
  Drawer,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from "../../hooks/use-auth";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ListIcon from "@mui/icons-material/List";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { usePermissionRedirect } from "./usePermissionRedirect";
import CloseIcon from "@mui/icons-material/Close";
import usePermissionCheck from "./usePermissionCheck";
import REACT_APP_LOGO_NAVBAR from './../../images/Logo_Kalanke_Blanc.png';

// Couleurs principales
const SIDEBAR_BG = "#8282CA";

// Liste des composants pour la vérification des permissions
const ALL_PERMISSION_COMPONENTS = [
  "Scores", "Apprenants", "Forum", "Actions", "EPP", "Satisfaction",
  "Liste des sessions", "Agenda", "Experts", "Administrateurs",
  "Activités", "Paramètres", "Rôles",
];

export const DashboardSidebar = (props) => {
  // isPending et startTransition viennent de DashboardLayout (useTransition remonté)
  // pour que la zone de contenu puisse aussi réagir à la navigation.
  const { openSide, setOpenSide, isPending, startTransition, ...other } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
    noSsr: true,
  });

  usePermissionCheck();
  usePermissionRedirect(ALL_PERMISSION_COMPONENTS);

  const tenant = useMemo(() => globalThis.localStorage.getItem('tenant'), []);
  const queryClient = useQueryClient();

  // ─── Prefetch au survol ───────────────────────────────────────────────────
  // Quand la souris entre sur un item, on déclenche le chargement des données
  // en arrière-plan. Résultat : quand l'utilisateur clique, les données sont
  // déjà dans le cache React Query → affichage quasi-instantané.
  const STALE = 3 * 60 * 1000; // même staleTime que dans _app.js

  const handlePrefetch = useCallback((path) => {
    if (!path) return;

    // ── 1. Préchargement du chunk JS ────────────────────────────────────────
    // Trouve le segment de route correspondant et déclenche le download du bundle
    // AVANT que l'utilisateur clique. Au clic, le composant s'affiche immédiatement.
    const matchedSegment = Object.keys(CHUNK_PRELOADERS).find((seg) =>
      path.includes(`/${seg}`)
    );
    if (matchedSegment) CHUNK_PRELOADERS[matchedSegment]();

    // ── 2. Préchargement des données via React Query ────────────────────────
    if (!user?.admin_id) return;

    if (path.includes('/customers')) {
      queryClient.prefetchQuery({
        queryKey: ['customers', user.admin_id, '', 0, 25],
        queryFn: () => userApi.getUsers(user.admin_id, 0, 25),
        staleTime: STALE,
      });
    } else if (path.includes('/epp/audits')) {
      queryClient.prefetchQuery({
        queryKey: ['audits', user.admin_id],
        queryFn: () => userApi.getEppByType(user.admin_id, 'audit'),
        staleTime: STALE,
      });
    } else if (path.includes('/epp/tcs')) {
      queryClient.prefetchQuery({
        queryKey: ['tcs', user.admin_id],
        queryFn: () => userApi.getEppByType(user.admin_id, 'tcs'),
        staleTime: STALE,
      });
    } else if (path.includes('/session')) {
      queryClient.prefetchQuery({
        queryKey: ['sessions', user.admin_id],
        queryFn: () => sessionsApi.getAllSessions(user.admin_id),
        staleTime: STALE,
      });
    } else if (path.includes('/epp/tcs')) {
      queryClient.prefetchQuery({
        queryKey: ['tcs', user.admin_id],
        queryFn: () => userApi.getEppByType(user.admin_id, 'tcs'),
        staleTime: STALE,
      });
    } else if (path.includes('/satisfaction')) {
      queryClient.prefetchQuery({
        queryKey: ['surveys', user.admin_id],
        queryFn: () => surveyApi.getAllSurvey(user.admin_id),
        staleTime: STALE,
      });
    } else if (path.includes('/actions')) {
      queryClient.prefetchQuery({
        queryKey: ['formations', user.admin_id],
        queryFn: () => formationsApi.getAllFormations(user.admin_id),
        staleTime: STALE,
      });
    }
  }, [queryClient, user?.admin_id, STALE]);

  const hasPermission = useCallback(
    (component, requiredPermission) =>
      user.components_and_permissions.some(
        (perm) => perm.component === component && perm.permission === requiredPermission
      ),
    [user.components_and_permissions]
  );

  // Construction des sections en fonction du service de l'utilisateur
  const sections = useMemo(() => {
    if (user?.id_service === 0) {
      return [
        {
          title: null,
          items: [
            {
              icon: <HomeIcon fontSize="small" />,
              title: "Tableau de bord",
              path: `/${tenant}/dashboard`,
            },
          ],
        },
        {
          title: "Analyses",
          icon: <TroubleshootOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Scores", "Voir") && { title: "Scores", path: `/${tenant}/dashboard/scores` },
            hasPermission("Performances", "Voir") && { title: "Performances" },
          ].filter(Boolean),
        },
        {
          title: "Apprenants",
          icon: <GroupOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Apprenants", "Voir") && { title: "Liste des apprenants", path: `/${tenant}/dashboard/customers` },
            hasPermission("Apprenants", "Créer") && { title: "Créer un apprenant", path: `/${tenant}/dashboard/create/customers` },
            hasPermission("Apprenants", "Créer") && { title: "Groupes", path: `/${tenant}/dashboard/group` },
            hasPermission("Forum", "Voir") && { title: "Forum", path: `/${tenant}/dashboard/forum` },
          ].filter(Boolean),
        },
        {
          title: "Conception",
          icon: <AssessmentOutlinedIcon fontSize="small" />,
          items: [
            (hasPermission("Actions", "Voir") || hasPermission("Actions", "Créer")) && { title: "Actions", path: `/${tenant}/dashboard/actions` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "Audits", path: `/${tenant}/dashboard/epp/audits` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "TCS", path: `/${tenant}/dashboard/epp/tcs` },
            hasPermission("Dossier patient", "Voir") && { title: "Dossiers patients", path: `/${tenant}/dashboard/patients_folder` },
            hasPermission("Gestion des Risques", "Voir") && { title: "Gestion des Risques" },
          ].filter(Boolean),
        },
        {
          title: "Qualité",
          icon: <VerifiedOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Satisfaction", "Voir") && { title: "Satisfaction", path: `/${tenant}/dashboard/satisfaction` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "Questionnaires Formation", path: `/${tenant}/dashboard/formation-questionnaire` },
          ].filter(Boolean),
        },
        {
          title: "Sessions",
          icon: <Groups2OutlinedIcon fontSize="small" />,
          items: [
            (hasPermission("Sessions", "Voir") || hasPermission("Sessions", "Créer")) && { title: "Liste des sessions", path: `/${tenant}/dashboard/session` },
            hasPermission("Agenda", "Voir") && { title: "Agenda", path: `/${tenant}/dashboard/agenda` },
          ].filter(Boolean),
        },
        {
          title: "Experts",
          icon: <WorkOutlineIcon fontSize="small" />,
          items: [
            hasPermission("Experts", "Voir") && { title: "Liste des experts", path: `/${tenant}/dashboard/experts` },
            { title: "Comité scientifique" },
          ].filter(Boolean),
        },
        {
          title: "Administration",
          icon: <AdminPanelSettingsIcon fontSize="small" />,
          items: [
            hasPermission("Administrateurs", "Voir") && { title: "Liste des administrateurs", path: `/${tenant}/dashboard/admins` },
            hasPermission("Activités", "Voir") && { title: "Activités", path: `/${tenant}/dashboard/activities` },
            hasPermission("Administrateurs", "Créer") && { title: "Créer un admin", path: `/${tenant}/dashboard/create/admin` },
          ].filter(Boolean),
        },
        {
          title: "Règlages",
          icon: <SettingsIcon fontSize="small" />,
          items: [
            hasPermission("Paramètres", "Voir") && { title: "Paramètres", path: `/${tenant}/dashboard/parametres` },
            hasPermission("Apprenants", "Créer") && { title: "Import", path: `/${tenant}/dashboard/import` },
            hasPermission("Rôles", "Voir") && { title: "Rôles", path: `/${tenant}/dashboard/roles` },
          ].filter(Boolean),
        },
      ].filter((section) => section.items.length > 0);
    }

    if (user?.id_service === 1) {
      return [
        {
          title: "Analyses",
          icon: <HomeIcon fontSize="small" />,
          items: [
            { title: "Tableau de bord", path: `/${tenant}/dashboard` },
            { title: "Agenda", path: `/${tenant}/dashboard/agenda` },
          ],
        },
        {
          title: "Évaluations",
          icon: <VerifiedOutlinedIcon fontSize="small" />,
          items: [
            { title: "Audits", path: `/${tenant}/dashboard/audits` },
            { title: "TCS", path: `/${tenant}/dashboard/tcs` },
            { title: "Apprenants", path: `/${tenant}/dashboard/customers` },
          ],
        },
        {
          title: "Sessions",
          icon: <ListIcon fontSize="small" />,
          items: [{ title: "Liste des sessions", path: `/${tenant}/dashboard/session` }],
        },
        {
          title: "Autres",
          icon: <ForumIcon fontSize="small" />,
          items: [{ title: "Forum", path: `/${tenant}/dashboard/forum` }],
        },
      ].filter((section) => section.items.length > 0);
    }

    return [];
  }, [user, tenant, hasPermission]);

  const handleClose = useCallback(() => setOpenSide(false), [setOpenSide]);

  // Détermine si un chemin correspond à l'URL courante
  const isPathActive = useCallback(
    (path) => path && location.pathname === path,
    [location.pathname]
  );

  // Navigation SPA sans rechargement.
  // startTransition indique à React que c'est une mise à jour non-urgente :
  // la page courante reste affichée jusqu'à ce que le nouveau chunk JS soit
  // téléchargé et prêt — le fallback Suspense ne s'affiche jamais.
  const handleNavigate = useCallback(
    (path) => {
      if (path) {
        startTransition(() => {
          navigate(path);
        });
        if (!lgUp) handleClose();
      }
    },
    [navigate, lgUp, handleClose, startTransition]
  );

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Barre de progression en haut de la sidebar pendant la navigation */}
      {isPending && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)",
            backgroundSize: "200% 100%",
            animation: "navProgress 0.8s ease-in-out infinite",
            "@keyframes navProgress": {
              "0%":   { backgroundPosition: "-200% 0" },
              "100%": { backgroundPosition: "200% 0" },
            },
            zIndex: 10,
          }}
        />
      )}
      {/* En-tête : logo et tenant */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box
          component="img"
          sx={{ width: "140px", height: "auto", display: "block" }}
          alt="Kalanke"
          src={REACT_APP_LOGO_NAVBAR}
        />
        {tenant && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              mt: 1.5,
              px: 1.5,
              py: 0.4,
              backgroundColor: "rgba(255,255,255,0.18)",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.95)",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "1px",
              }}
            >
              {tenant.toUpperCase()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 1.5,
          py: 1.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {sections.map((section) => {
          // Sections sans titre (comme "Tableau de bord") – rendu direct des items
          if (section.title === null) {
            return section.items.map((item) => {
              const active = isPathActive(item.path);
              return (
                <Box
                  key={String(item.title)}
                  onClick={() => handleNavigate(item.path)}
                  onMouseEnter={() => handlePrefetch(item.path)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.125,
                    mb: 0.5,
                    borderRadius: "10px",
                    cursor: item.path ? "pointer" : "default",
                    backgroundColor: active
                      ? "rgba(255,255,255,0.22)"
                      : "transparent",
                    borderLeft: active
                      ? "3px solid rgba(255,255,255,0.9)"
                      : "3px solid transparent",
                    transition: "background-color 0.2s ease, border-left 0.2s ease",
                    "&:hover": item.path
                      ? { backgroundColor: "rgba(255,255,255,0.15)" }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "rgba(255,255,255,0.9)",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: active ? 700 : 500,
                      color: "white",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              );
            });
          }

          // Sections normales avec titre et sous‑items toujours visibles
          return (
            <Box key={section.title} sx={{ mb: 1 }}>
              {/* En-tête de section (non cliquable) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.125,
                  borderRadius: "10px",
                  backgroundColor: "transparent",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "rgba(255,255,255,0.85)",
                    flexShrink: 0,
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "white",
                  }}
                >
                  {section.title}
                </Typography>
              </Box>

              {/* Sous‑items affichés en permanence */}
              <Box sx={{ pt: 0.5, pb: 0.5, pl: 0.5 }}>
                {section.items.map((item) => {
                  const active = isPathActive(item.path);
                  return (
                    <Box
                      key={item.title}
                      onClick={() => handleNavigate(item.path)}
                      onMouseEnter={() => handlePrefetch(item.path)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pl: 4.5,
                        pr: 1.5,
                        py: 0.875,
                        mb: 0.25,
                        borderRadius: "8px",
                        cursor: item.path ? "pointer" : "default",
                        opacity: item.path ? 1 : 0.5,
                        backgroundColor: active
                          ? "rgba(255,255,255,0.18)"
                          : "transparent",
                        borderLeft: active
                          ? "3px solid rgba(255,255,255,0.85)"
                          : "3px solid transparent",
                        transition: "all 0.15s ease",
                        "&:hover": item.path
                          ? { backgroundColor: "rgba(255,255,255,0.1)" }
                          : {},
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: active ? 600 : 400,
                          color: active
                            ? "white"
                            : "rgba(255,255,255,0.8)",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // Version desktop (drawer permanent)
  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: SIDEBAR_BG,
            borderRight: "none",
            color: "white",
            width: 240,
            boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
          },
        }}
        variant="persistent"
      >
        {content}
      </Drawer>
    );
  }

  // Version mobile (drawer temporaire)
  return (
    <>
      <Drawer
        anchor="left"
        open={openSide}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: SIDEBAR_BG,
            borderRight: "none",
            color: "white",
            width: 240,
            boxShadow: "4px 0 16px rgba(0,0,0,0.2)",
            top: "64px",
            height: "calc(100% - 64px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1,
            pt: 0.75,
            pb: 0,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              "&:hover": {
                color: "white",
                backgroundColor: "rgba(255,255,255,0.15)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {content}
      </Drawer>
      <ToastContainer />
    </>
  );
};

DashboardSidebar.propTypes = {
  openSide: PropTypes.bool,
  setOpenSide: PropTypes.func,
};

export default DashboardSidebar;